import { PUBLIC_IMG_HOST } from "$env/static/public";
import { getPlaceHolderImgUrl } from "mock/image";
import type { ImageDetails } from "proto/image";
import type { HTMLImgAttributes } from "svelte/elements";

type ParyImageDetails = { base_url: string, filename: string, provider?: string };
export type MaybeImageDetails = ImageDetails | undefined;

export function thumbor(image: ParyImageDetails | undefined, w = 0, h = 0): string {
	if (!image || !image.base_url || !image.filename) return '';
	if (image.provider !== 'thumbor') { w = 0; h = 0; } // if not thumbor, ignore dimentions
	let { base_url:a, filename:b } = image;
	if (a.endsWith('/')) a = a.slice(0,-1);
	if (b.startsWith('/')) b = b.slice(1);
	const parts = [a];
	if (w||h) parts.push(`${w}x${h}`);
	parts.push(b);
	return parts.join('/');
}

export function baseAndFileName(image: { base_url: string, filename: string }, w = 0, h = 0) {
	const s: string = w||h ? `${w}x${h}` : '';
	return `${image.base_url}/${s}${image.filename}`;
}

/**
 * Original thumbor method
 * @deprecated use toSrc instead
 */
export function imgSrc(img:{name:string}):string;
export function imgSrc(name?:string):string;
export function imgSrc(img:{name:string}, dimentions:string):string;
export function imgSrc(name?:string, dimentions?:string):string;
export function imgSrc(img:{name:string}, w:number, h:number):string;
export function imgSrc(name?:string, w?:number, h?:number):string;
export function imgSrc(name?:string|{name:string}, w?:number|string, h = 0) {
	if (typeof name === 'object' && name) name = name.name;
	if (!name) return getPlaceHolderImgUrl('[not set]', 120, 60);
	if (typeof w === 'string') return `${PUBLIC_IMG_HOST}/${w}/${name}`;
	if (typeof w === 'number') return `${PUBLIC_IMG_HOST}/${w}x${h}/${name}`;
	return `${PUBLIC_IMG_HOST}/${name}`;
}

/**
 * Thumbor image url generator,
 * adds pixel density using srcset
 * @param img
 * @param w percentage of viewport on desktop
 * @returns an object containing src, srcset and sizes that can be spread into an img tag
 */
export function imgSrcSet(img:{name:string}): Pick<HTMLImgAttributes, 'src' | 'sizes' | 'srcset'>;
export function imgSrcSet(name?:string): Pick<HTMLImgAttributes, 'src' | 'sizes' | 'srcset'>;
export function imgSrcSet(img:{name:string}, w:number): Pick<HTMLImgAttributes, 'src' | 'sizes' | 'srcset'>;
export function imgSrcSet(name?:string, w?:number): Pick<HTMLImgAttributes, 'src' | 'sizes' | 'srcset'>;
export function imgSrcSet(name?:string|{name:string}|MaybeImageDetails, w = 1): Pick<HTMLImgAttributes, 'src' | 'sizes' | 'srcset'> {
	if (typeof name === 'object' && name) name = (name as {name:string}).name;
	if (!name) return {
		src: getPlaceHolderImgUrl('[not set]', 120, 60),
		sizes: '100vw',
	};
	if (w > 1) w = 1;
	return {
		src: `${PUBLIC_IMG_HOST}/600x0/${name}`,
		srcset: `
			${PUBLIC_IMG_HOST}/800x0/${name} 800w,
			${PUBLIC_IMG_HOST}/${w * 1000}x0/${name} 1000w,
			${PUBLIC_IMG_HOST}/${w * 1200}x0/${name} 1200w,
			${PUBLIC_IMG_HOST}/${w * 1400}x0/${name} 1400w,
			${PUBLIC_IMG_HOST}/${w * 1600}x0/${name} 1600w,
			${PUBLIC_IMG_HOST}/${w * 1800}x0/${name} 1800w,
			${PUBLIC_IMG_HOST}/${w * 2000}x0/${name} 2000w,
			${PUBLIC_IMG_HOST}/${w * 2600}x0/${name} 2600w,
			${PUBLIC_IMG_HOST}/${w * 3000}x0/${name} 3000w,
			${PUBLIC_IMG_HOST}/${w * 3500}x0/${name} 3500w,
			${PUBLIC_IMG_HOST}/${w * 4000}x0/${name} 4000w,
		`,
		sizes: '100vw',
	};
}

/**
 * Take a few possible inputs and return a URL to an image.
 * @param image An image's complete URL or details of the image
 * @param width Desired width set in the returned image url.
 * @param height Desired height set in the returned image url.
 * @returns A URL ready to be assigned to an image's src attribute.
 */
export function toSrc(image: MaybeImageDetails|string, width?: number, height?:number) {
	// no input - no url
	if (typeof image === 'undefined') return '';

	// string input - is the url (if it has dimentions, replace them)
	if (typeof image === 'string') {
		if (typeof width !== 'number') return image; // if no with is provided then don't risk modifying the existing dimention in the url
		return image.replace(/\/[0-9]+x[0-9]+\//,`/${width??0}x${height??0}/`);
	}

	if (typeof image === 'object') {
		if (image?.base_url && image?.filename) {
			return thumbor(image, width, height);
		}
		return image.raw_image ?? image?.filename ?? image?.base_url ?? '';
	}
	return '';
}